import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, InputAdornment, Pagination, Paper, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import SearchIcon from '@mui/icons-material/Search';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { usePermissions } from '../../../utils/usePermissions';
import { ConfirmDialog, EditEmptyState } from '../reports/edit/EditCommon';
import { ACTION_BUTTON_SX } from '../reports/summary/summaryStyles';
import { PRIMARY_BUTTON_SX } from '../mathReports/mathTableStyles';
import RegionFormDialog from './regions/RegionFormDialog';
import RegionsTable from './regions/RegionsTable';
import { filterRegions, pageCount, pageSlice } from './regions/regionsCalc';
import type { Region } from './regions/regionsApi';
import { useRegions } from './regions/useRegions';

const BORDER = '1px solid #e2e8f0';

/** Форма и диалог удаления остаются смонтированными на время закрытия, чтобы текст не пропадал посреди анимации */
interface FormState {
  key: number;
  target: Region | null;
  open: boolean;
}

interface DeleteState {
  region: Region;
  open: boolean;
}

const RegionManager: React.FC = () => {
  const { hasPerm } = usePermissions();
  const canCreate = hasPerm('tables.regions.create');
  const canEdit = hasPerm('tables.regions.edit');
  const canDelete = hasPerm('tables.regions.delete');

  const { regions, loading, error, retry, pending, saveRegion, removeRegion } = useRegions();

  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [form, setForm] = useState<FormState | null>(null);
  const [removal, setRemoval] = useState<DeleteState | null>(null);

  useEffect(() => {
    document.title = 'Список регионов | Zerde';
  }, []);

  const filtered = useMemo(() => (regions ? filterRegions(regions, query) : []), [regions, query]);
  const pages = pageCount(filtered.length);
  // После удаления или поиска страница может оказаться за последней
  const currentPage = Math.min(page, pages);
  const visible = useMemo(() => pageSlice(filtered, currentPage), [filtered, currentPage]);

  const handleQueryChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
    setPage(1);
  }, []);
  const handlePageChange = useCallback((_: React.ChangeEvent<unknown>, value: number) => setPage(value), []);

  // Каждое открытие формы получает новый key: поле заполняется заново, а не хранит прежний ввод
  const openForm = useCallback(
    (target: Region | null) => setForm((prev) => ({ key: (prev ? prev.key : 0) + 1, target, open: true })),
    []
  );
  const handleCreate = useCallback(() => openForm(null), [openForm]);
  const closeForm = useCallback(() => setForm((prev) => prev && { ...prev, open: false }), []);

  const handleAskDelete = useCallback((region: Region) => setRemoval({ region, open: true }), []);
  const closeRemoval = useCallback(() => setRemoval((prev) => prev && { ...prev, open: false }), []);

  const handleConfirmDelete = useCallback(async () => {
    if (!removal) return;
    if (await removeRegion(removal.region)) closeRemoval();
  }, [removal, removeRegion, closeRemoval]);

  const renderContent = () => {
    if (error && !regions) {
      return (
        <EditEmptyState
          icon={<ErrorOutlineIcon />}
          title="Не удалось загрузить регионы"
          text={error}
          action={<Button variant="outlined" onClick={retry} sx={ACTION_BUTTON_SX}>Повторить</Button>}
        />
      );
    }
    if (!regions) {
      return (
        <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      );
    }
    if (regions.length === 0) {
      return (
        <EditEmptyState
          icon={<LocationCityIcon />}
          title="Регионов пока нет"
          text={canCreate ? 'Добавьте первый регион: он появится в профилях сотрудников и в списке терминалов.' : undefined}
          action={
            canCreate ? (
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate} sx={PRIMARY_BUTTON_SX}>
                Добавить регион
              </Button>
            ) : undefined
          }
        />
      );
    }
    if (filtered.length === 0) {
      return (
        <EditEmptyState
          icon={<SearchOffIcon />}
          title="Ничего не найдено"
          text="Регионов с таким названием нет. Проверьте запрос."
        />
      );
    }
    return (
      <>
        <Paper
          elevation={0}
          sx={{ border: BORDER, borderRadius: '12px', overflow: 'hidden', transition: 'opacity 0.15s ease' }}
          style={{ opacity: loading ? 0.55 : 1 }}
        >
          <RegionsTable regions={visible} canEdit={canEdit} canDelete={canDelete} onEdit={openForm} onDelete={handleAskDelete} />
        </Paper>
        {pages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination count={pages} page={currentPage} onChange={handlePageChange} shape="rounded" color="primary" />
          </Box>
        )}
      </>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, maxWidth: 450 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Поиск по названию региона"
            value={query}
            onChange={handleQueryChange}
            disabled={!regions}
            sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#ffffff', borderRadius: '8px' } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: '#94a3b8' }} />
                </InputAdornment>
              )
            }}
          />
          {regions && (
            <Typography variant="body2" sx={{ color: '#64748b', whiteSpace: 'nowrap' }}>
              Всего: <b>{filtered.length}</b>
            </Typography>
          )}
        </Box>

        {canCreate && regions && regions.length > 0 && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate} sx={PRIMARY_BUTTON_SX}>
            Добавить регион
          </Button>
        )}
      </Box>

      {renderContent()}

      {form && regions && (
        <RegionFormDialog
          key={form.key}
          open={form.open}
          target={form.target}
          regions={regions}
          saving={pending === 'save'}
          onSave={saveRegion}
          onClose={closeForm}
        />
      )}

      <ConfirmDialog
        open={!!removal && removal.open}
        title="Удалить регион"
        confirmText="Удалить"
        confirmColor="error"
        loading={pending === 'delete'}
        maxWidth="xs"
        onConfirm={handleConfirmDelete}
        onClose={closeRemoval}
      >
        {removal && (
          <>
            <Typography sx={{ color: '#334155', fontSize: '0.92rem', mb: 1 }}>
              Удалить регион <b>«{removal.region.name}»</b>?
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '0.86rem' }}>
              Регион пропадет из справочника. Сотрудников и терминалов в нем нет, их данные не затрагиваются.
            </Typography>
          </>
        )}
      </ConfirmDialog>
    </Box>
  );
};

export default RegionManager;
